/**
 * Create / Edit Savings Goal Modal
 * Savings Plan section: radio selects which field is calculated from the other two.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useCreateSavingsGoal, useUpdateSavingsGoal } from '../../hooks/useBudgetOverview';
import {
  calculateAmountToSave,
  calculateMonthlyTargetFromPlan,
  calculateTargetAmountFromPlan,
  calculateTargetDateFromPlan,
} from '../../utils/budget-calculations';
import type {
  SavingsGoal,
  GoalCategoryType,
  GoalPriority,
  CreateSavingsGoalInput,
} from '../../types/savings-goal.types';

interface CreateGoalModalProps {
  goal?: SavingsGoal | null;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

type CalculatedField = 'targetAmount' | 'targetDate' | 'monthlyTarget';

const CATEGORY_OPTIONS: { value: GoalCategoryType; label: string }[] = [
  { value: 'safety_net', label: 'Safety Net' },
  { value: 'travel', label: 'Travel' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'housing', label: 'Housing' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS: { value: GoalPriority; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const CALCULATE_OPTIONS: { value: CalculatedField; label: string }[] = [
  { value: 'targetAmount', label: 'Target Amount' },
  { value: 'targetDate', label: 'Target Date' },
  { value: 'monthlyTarget', label: 'Monthly Target' },
];

const inputBaseClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100';
const inputEnabledClass = `${inputBaseClass} bg-white dark:bg-gray-700`;
const inputDisabledClass = `${inputBaseClass} bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 cursor-not-allowed`;

function formatMoney(amount: number): string {
  return amount.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

export function CreateGoalModal({
  goal,
  onClose,
  onDirtyChange,
}: CreateGoalModalProps): React.ReactElement {
  const isEdit = !!goal;
  const closeRef = useRef<HTMLButtonElement>(null);
  const createGoal = useCreateSavingsGoal();
  const updateGoal = useUpdateSavingsGoal();

  const [name, setName] = useState(goal?.name ?? '');
  const [categoryType, setCategoryType] = useState<GoalCategoryType>(goal?.categoryType ?? 'other');
  const [startingAmount, setStartingAmount] = useState(
    goal != null ? String(goal.currentAmount) : '0'
  );
  const [targetAmount, setTargetAmount] = useState(goal ? String(goal.targetAmount) : '');
  const [targetDate, setTargetDate] = useState(goal?.targetDate ?? '');
  const [priority, setPriority] = useState<GoalPriority>(goal?.priority ?? 'medium');
  const [monthlyTarget, setMonthlyTarget] = useState(goal?.monthlyTarget ? String(goal.monthlyTarget) : '');
  const [calculateField, setCalculateField] = useState<CalculatedField>('monthlyTarget');
  const [calcError, setCalcError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startingNum = (() => {
    const n = parseFloat(startingAmount);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();

  useEffect(() => {
    onDirtyChange?.(true);
  }, [name, categoryType, startingAmount, targetAmount, targetDate, priority, monthlyTarget, calculateField, onDirtyChange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Live recalculation for the selected calculated field
  useEffect(() => {
    const amountNum = parseFloat(targetAmount);
    const monthlyNum = parseFloat(monthlyTarget);
    const hasAmount = Number.isFinite(amountNum) && amountNum > 0;
    const hasMonthly = Number.isFinite(monthlyNum) && monthlyNum > 0;
    const hasDate = Boolean(targetDate);

    const setNumberIfChanged = (
      current: string,
      setter: React.Dispatch<React.SetStateAction<string>>,
      value: number
    ) => {
      const next = String(value);
      if (current !== next) setter(next);
    };

    if (calculateField === 'monthlyTarget') {
      if (!hasAmount || !hasDate) {
        setCalcError(null);
        return;
      }
      const amountToSave = calculateAmountToSave(amountNum, startingNum);
      if (amountToSave <= 0) {
        setNumberIfChanged(monthlyTarget, setMonthlyTarget, 0);
        setCalcError(startingNum > 0 ? 'Goal is already fully funded' : null);
        return;
      }
      const result = calculateMonthlyTargetFromPlan({ amountToSave, targetDate });
      if ('error' in result) {
        setCalcError(result.error);
        return;
      }
      setCalcError(null);
      setNumberIfChanged(monthlyTarget, setMonthlyTarget, result.value as number);
      return;
    }

    if (calculateField === 'targetDate') {
      if (!hasAmount || !hasMonthly) {
        setCalcError(null);
        return;
      }
      const amountToSave = calculateAmountToSave(amountNum, startingNum);
      if (amountToSave <= 0) {
        setCalcError(startingNum > 0 ? 'Goal is already fully funded' : null);
        return;
      }
      const result = calculateTargetDateFromPlan({ amountToSave, monthlyTarget: monthlyNum });
      if ('error' in result) {
        setCalcError(result.error);
        return;
      }
      setCalcError(null);
      const next = String(result.value);
      if (targetDate !== next) setTargetDate(next);
      return;
    }

    // calculateField === 'targetAmount'
    if (!hasDate || !hasMonthly) {
      setCalcError(null);
      return;
    }
    const result = calculateTargetAmountFromPlan({
      monthlyTarget: monthlyNum,
      targetDate,
      startingAmount: startingNum,
    });
    if ('error' in result) {
      setCalcError(result.error);
      return;
    }
    setCalcError(null);
    setNumberIfChanged(targetAmount, setTargetAmount, result.value as number);
  }, [calculateField, targetAmount, targetDate, monthlyTarget, startingNum]);

  const remainingToSave =
    Number.isFinite(parseFloat(targetAmount)) && parseFloat(targetAmount) > 0
      ? calculateAmountToSave(parseFloat(targetAmount), startingNum)
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const amount = parseFloat(targetAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Target amount must be greater than zero');
      return;
    }
    if (startingNum < 0) {
      setError('Starting amount cannot be negative');
      return;
    }
    if (startingNum > amount) {
      setError('Starting amount cannot exceed target amount');
      return;
    }
    if (!targetDate) {
      setError('Target date is required');
      return;
    }

    const input: CreateSavingsGoalInput = {
      name: name.trim(),
      categoryType,
      targetAmount: amount,
      targetDate,
      priority,
      monthlyTarget: monthlyTarget ? parseFloat(monthlyTarget) : undefined,
      startingAmount: startingNum,
    };

    try {
      if (isEdit && goal) {
        await updateGoal.mutateAsync({ id: goal.id, input });
      } else {
        await createGoal.mutateAsync(input);
      }
      onDirtyChange?.(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save goal');
    }
  };

  const isPending = createGoal.isPending || updateGoal.isPending;
  const amountDisabled = calculateField === 'targetAmount';
  const dateDisabled = calculateField === 'targetDate';
  const monthlyDisabled = calculateField === 'monthlyTarget';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="create-goal-title">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 id="create-goal-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEdit ? 'Edit Goal' : 'New Goal'}
            </h2>
            <button ref={closeRef} type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="goal-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Goal Name</label>
              <input id="goal-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required className={inputEnabledClass} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="goal-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select id="goal-category" value={categoryType} onChange={(e) => setCategoryType(e.target.value as GoalCategoryType)} className={inputEnabledClass}>
                  {CATEGORY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="goal-priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                <select id="goal-priority" value={priority} onChange={(e) => setPriority(e.target.value as GoalPriority)} className={inputEnabledClass}>
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Savings Plan */}
            <fieldset className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
              <legend className="px-1 text-sm font-semibold text-gray-900 dark:text-gray-100">Savings Plan</legend>

              <div>
                <span id="calculate-field-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Calculate:
                </span>
                <div
                  role="radiogroup"
                  aria-labelledby="calculate-field-label"
                  className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4"
                >
                  {CALCULATE_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="calculate-field"
                        value={opt.value}
                        checked={calculateField === opt.value}
                        onChange={() => {
                          setCalcError(null);
                          setCalculateField(opt.value);
                        }}
                        className="text-green-600 focus:ring-green-500"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Enter the two fields you know. The selected field is calculated automatically.
                </p>
              </div>

              <div>
                <label htmlFor="goal-starting" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Starting Amount (AUD)
                </label>
                <input
                  id="goal-starting"
                  type="number"
                  min={0}
                  step="0.01"
                  value={startingAmount}
                  onChange={(e) => setStartingAmount(e.target.value)}
                  className={inputEnabledClass}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Amount already saved toward this goal. Defaults to 0.
                </p>
              </div>

              <div>
                <label htmlFor="goal-target" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Target Amount (AUD)
                  {amountDisabled && (
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">(calculated)</span>
                  )}
                </label>
                <input
                  id="goal-target"
                  type="number"
                  min={0.01}
                  step="0.01"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  required={!amountDisabled}
                  readOnly={amountDisabled}
                  disabled={amountDisabled}
                  className={amountDisabled ? inputDisabledClass : inputEnabledClass}
                  aria-readonly={amountDisabled}
                />
                {startingNum > 0 && remainingToSave != null && !amountDisabled && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Based on {formatMoney(remainingToSave)} remaining to save
                    {` (${formatMoney(startingNum)} already saved)`}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="goal-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Target Date
                  {dateDisabled && (
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">(calculated)</span>
                  )}
                </label>
                <input
                  id="goal-date"
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  required={!dateDisabled}
                  readOnly={dateDisabled}
                  disabled={dateDisabled}
                  className={dateDisabled ? inputDisabledClass : inputEnabledClass}
                  aria-readonly={dateDisabled}
                />
              </div>

              <div>
                <label htmlFor="goal-monthly" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly Target
                  {monthlyDisabled && (
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">(calculated)</span>
                  )}
                </label>
                <input
                  id="goal-monthly"
                  type="number"
                  min={0}
                  step="0.01"
                  value={monthlyTarget}
                  onChange={(e) => setMonthlyTarget(e.target.value)}
                  readOnly={monthlyDisabled}
                  disabled={monthlyDisabled}
                  className={monthlyDisabled ? inputDisabledClass : inputEnabledClass}
                  aria-readonly={monthlyDisabled}
                />
              </div>

              {calcError && (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {calcError}
                </p>
              )}
            </fieldset>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button type="submit" disabled={isPending} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save Changes' : 'Create Goal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
